(function() {
  'use strict';

  function createSlidingWindowRateLimiter(maxRequests, windowMs) {
    var requestTimestamps = [];
    var retryAfter = 0;

    return {
      canMakeRequest: function() {
        var now = Date.now();
        requestTimestamps = requestTimestamps.filter(function(ts) {
          return now - ts < windowMs;
        });
        if (retryAfter > now) {
          return false;
        }
        return requestTimestamps.length < maxRequests;
      },
      recordRequest: function() {
        requestTimestamps.push(Date.now());
      },
      setRetryAfter: function(ms) {
        retryAfter = Date.now() + ms;
      },
      getRetryAfter: function() {
        return Math.max(0, retryAfter - Date.now());
      },
      clear: function() {
        requestTimestamps = [];
        retryAfter = 0;
      }
    };
  }

  function createRequestQueue() {
    var queue = [];
    var isProcessing = false;
    var currentAbortController = null;

    function processNext() {
      if (queue.length === 0) {
        isProcessing = false;
        return;
      }

      isProcessing = true;
      var task = queue.shift();

      currentAbortController = new AbortController();
      task.abortController = currentAbortController;

      task.execute(currentAbortController.signal)
        .then(function(result) {
          task.resolve(result);
        })
        .catch(function(error) {
          if (error.name !== 'AbortError') {
            task.reject(error);
          }
        })
        .finally(function() {
          currentAbortController = null;
          processNext();
        });
    }

    return {
      enqueue: function(taskFn) {
        return new Promise(function(resolve, reject) {
          queue.push({
            execute: taskFn,
            resolve: resolve,
            reject: reject,
            abortController: null
          });
          if (!isProcessing) {
            processNext();
          }
        });
      },
      abortCurrent: function() {
        if (currentAbortController) {
          currentAbortController.abort();
        }
      },
      clear: function() {
        queue = [];
        this.abortCurrent();
      }
    };
  }

  var CartData = (function() {
    var cache = null;
    var cacheTime = 0;
    var cacheDuration = 5000;
    var rateLimiter = createSlidingWindowRateLimiter(30, 60000);
    var requestQueue = createRequestQueue();

    function safeJsonParse(response) {
      return response.text().then(function(text) {
        try {
          return JSON.parse(text);
        } catch (e) {
          throw new Error('Invalid JSON response');
        }
      });
    }

    function getCart() {
      var now = Date.now();

      if (cache && (now - cacheTime < cacheDuration)) {
        return Promise.resolve(cache);
      }

      if (!rateLimiter.canMakeRequest()) {
        var retryAfter = rateLimiter.getRetryAfter();
        console.warn('[CartData] Rate limited, wait ' + Math.ceil(retryAfter / 1000) + 's');
        return Promise.resolve(cache || { items: [] });
      }

      rateLimiter.recordRequest();

      return requestQueue.enqueue(function(signal) {
        return fetch('/cart.js', {
          credentials: 'same-origin',
          method: 'GET',
          signal: signal
        })
          .then(function(response) {
            if (response.status === 429) {
              var retryHeader = response.headers.get('Retry-After');
              var retryMs = retryHeader ? parseInt(retryHeader, 10) * 1000 : 5000;
              rateLimiter.setRetryAfter(retryMs);
              console.warn('[CartData] Rate limited by server, retry after ' + (retryMs / 1000) + 's');
              return cache || { items: [] };
            }
            if (!response.ok) {
              throw new Error('Failed to fetch cart: ' + response.status);
            }
            return safeJsonParse(response);
          });
      })
        .then(function(cart) {
          if (cart && typeof cart === 'object') {
            cache = cart;
            cacheTime = now;
          }
          return cache || { items: [] };
        })
        .catch(function(error) {
          console.error('[CartData] Error:', error);
          return cache || { items: [] };
        });
    }

    function invalidateCache() {
      cache = null;
      cacheTime = 0;
    }

    function updateCartItemCount(count) {
      if (cache) {
        cache.item_count = count;
      }
    }

    function destroy() {
      requestQueue.clear();
      rateLimiter.clear();
      cache = null;
      cacheTime = 0;
    }

    return {
      getCart: getCart,
      invalidateCache: invalidateCache,
      updateCartItemCount: updateCartItemCount,
      destroy: destroy
    };
  })();

  document.addEventListener('cart:updated', function(event) {
    if (event.detail && event.detail.cart) {
      CartData.cache = event.detail.cart;
      CartData.cacheTime = Date.now();
    }
  });

  document.addEventListener('ajaxProduct:added', function() {
    CartData.invalidateCache();
  });

  window.CartData = CartData;
})();
